import { md5hex } from './md5';

// VGA SignInMethod values used as login_channel in gt_verify events v2
export const LOGIN_CHANNEL = {
  UNKNOWN: 0,
  EMAIL: 1,
  PHONE_NUMBER: 2,
  FACEBOOK: 4,
  GOOGLE: 5,
  APPLE: 6,
  ZING: 7,
};

// Maps loginTypeName (used in Login.js handleRedirect) to LOGIN_CHANNEL value
const LOGIN_TYPE_TO_CHANNEL = {
  email: LOGIN_CHANNEL.EMAIL,
  zalo: LOGIN_CHANNEL.UNKNOWN,
  facebook: LOGIN_CHANNEL.FACEBOOK,
  google: LOGIN_CHANNEL.GOOGLE,
  apple: LOGIN_CHANNEL.APPLE,
  zingid: LOGIN_CHANNEL.ZING,
};

const STORAGE_KEY = 'gt_verify_login_channel';

// Call this BEFORE any OAuth redirect so the channel survives navigation
export function saveLoginChannel(loginTypeName) {
  const ch = LOGIN_TYPE_TO_CHANNEL[loginTypeName] !== undefined
    ? LOGIN_TYPE_TO_CHANNEL[loginTypeName]
    : LOGIN_CHANNEL.UNKNOWN;
  sessionStorage.setItem(STORAGE_KEY, String(ch));
}

export function getLoginChannel() {
  const val = sessionStorage.getItem(STORAGE_KEY);
  return val !== null ? parseInt(val, 10) : LOGIN_CHANNEL.UNKNOWN;
}

const SESSION_ID_STORAGE_KEY = 'gt_verify_tracking_session_id';

// One tracking_session_id per browser tab session, generated once and reused
// for every event through the login + khai báo flow (survives SPA route
// changes and full-page OAuth redirects, same as saveLoginChannel above).
function getOrCreateTrackingSessionId(metadata) {
  let sessionId = sessionStorage.getItem(SESSION_ID_STORAGE_KEY);
  if (!sessionId) {
    sessionId = md5hex(
      String(metadata.game_id || '') + String(metadata.user_id || '') + String(Date.now())
    );
    sessionStorage.setItem(SESSION_ID_STORAGE_KEY, sessionId);
  }
  return sessionId;
}

// Call this on logout so the next login starts a fresh tracking_session_id
// instead of reusing the one from the previous login session.
export function clearTrackingSessionId() {
  sessionStorage.removeItem(SESSION_ID_STORAGE_KEY);
}

// Returns platform code matching oauth-web's getDevicePlatform():
// '1'=iOS, '2'=Android, '3'=Mac, '4'=Windows, '0'=other
function getDevicePlatform() {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  if (/iPhone|iPad|iPod/.test(ua)) return '1';
  if (/Android/.test(ua)) return '2';
  if (/Mac OS X/.test(ua)) return '3';
  if (/Windows NT/.test(ua)) return '4';
  return '0';
}

function parseUA() {
  const ua = navigator.userAgent || '';
  let browser = 'Unknown', bVer = 'Unknown';
  if (ua.includes('Edg/')) {
    browser = 'Edge';
    bVer = (ua.match(/Edg\/([\d]+)/) || [])[1] || 'Unknown';
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome';
    bVer = (ua.match(/Chrome\/([\d]+)/) || [])[1] || 'Unknown';
  } else if (ua.includes('Firefox/')) {
    browser = 'Firefox';
    bVer = (ua.match(/Firefox\/([\d]+)/) || [])[1] || 'Unknown';
  } else if (ua.includes('Safari/') && ua.includes('Version/')) {
    browser = 'Safari';
    bVer = (ua.match(/Version\/([\d]+)/) || [])[1] || 'Unknown';
  }

  let os = 'Unknown', osVer = 'Unknown';
  if (ua.includes('Android')) {
    os = 'Android';
    osVer = (ua.match(/Android ([\d.]+)/) || [])[1] || 'Unknown';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS';
    osVer = ((ua.match(/OS ([\d_]+)/) || [])[1] || '').replace(/_/g, '.') || 'Unknown';
  } else if (ua.includes('Windows')) {
    os = 'Windows';
    osVer = (ua.match(/Windows NT ([\d.]+)/) || [])[1] || 'Unknown';
  } else if (ua.includes('Mac OS X')) {
    os = 'Mac OS';
    osVer = ((ua.match(/Mac OS X ([\d_.]+)/) || [])[1] || '').replace(/_/g, '.') || 'Unknown';
  } else if (ua.includes('Linux')) {
    os = 'Linux';
  }

  return {
    model: `${browser}/${bVer}_${os}/${osVer}_browser`,
    os_version: osVer,
    ua,
  };
}

// Fire-and-forget POST to the VDA tracking endpoint.
// eventId format: "gt_verify.<screen>.<action>" (e.g. "gt_verify.273.000")
// metadata: object with game_id, user_id, event_name, login_channel, and any event-specific fields
export function trackGtVerify(eventId, metadata) {
  const endpoint = process.env.REACT_APP_TRACKING_ENDPOINT;
  const key = process.env.REACT_APP_TRACKING_KEY;
  if (!endpoint) return;

  const timestamp = Date.now();
  const { model, os_version, ua } = parseUA();
  const tracking_session_id = getOrCreateTrackingSessionId(metadata);
  const payload = {
    timestamp,
    event_id: eventId,
    tracking_session_id,
    platform: getDevicePlatform(),
    model,
    os_version,
    ua,
    metadata,
  };

  fetch(`${endpoint}/api/v1/event/push`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(key ? { Authorization: `Basic ${key}` } : {}),
    },
    body: JSON.stringify({ events: [JSON.stringify(payload)] }),
    keepalive: true,
  }).catch(() => {});
}
